import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../config';
import CollaboratorProfileModal from './CollaboratorProfileModal';

const EMPTY = { specialty: '', full_name: '', city: '', address: '', phones: '', email: '', treatments: '', photo_url: '', gesy_status: '', is_partner: false };

const photoSrc = (url) => (!url ? '' : (url.startsWith('http') ? url : `${BACKEND_URL}${url}`));

/**
 * Directory of medical collaborators for the Patient Line.
 * - Admin (readOnly=false): full CRUD incl. treatments + profile photo upload.
 * - Agent (readOnly=true): read-only list, grouped & sorted by city.
 */
const LineCollaboratorsPanel = ({ readOnly = false }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {url, name} | null
  const [profile, setProfile] = useState(null); // collaborator being viewed
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/line-collaborators`);
      setItems(data.collaborators || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setErr(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/line-collaborators/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, photo_url: data.photo_url }));
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Σφάλμα ανεβάσματος εικόνας');
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (!form.specialty.trim() || !form.full_name.trim()) {
      setErr('Απαιτούνται ειδικότητα και ονοματεπώνυμο'); return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/line-collaborators/${editingId}`, form);
        setMsg('Ο συνεργάτης ενημερώθηκε.');
      } else {
        await axios.post(`${API}/admin/line-collaborators`, form);
        setMsg('Ο συνεργάτης προστέθηκε.');
      }
      setForm(EMPTY); setEditingId(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ specialty: c.specialty || '', full_name: c.full_name || '', city: c.city || '', address: c.address || '', phones: c.phones || '', email: c.email || '', treatments: c.treatments || '', photo_url: c.photo_url || '', gesy_status: c.gesy_status || '', is_partner: !!c.is_partner });
    setErr(''); setMsg('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); setErr(''); setMsg(''); if (fileRef.current) fileRef.current.value = ''; };

  const remove = async (id, name) => {
    if (!window.confirm(`Διαγραφή συνεργάτη «${name}»;`)) return;
    try { await axios.delete(`${API}/admin/line-collaborators/${id}`); load(); } catch (_e) { /* ignore */ }
  };

  // Group by city (already sorted by backend: city → specialty → name)
  const groups = items.reduce((acc, c) => {
    const key = (c.city || '').trim() || 'Χωρίς πόλη';
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});
  const cityNames = Object.keys(groups);

  const GESY = {
    in:   { label: 'Εντός ΓεΣΥ',        cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-500/40' },
    out:  { label: 'Εκτός ΓεΣΥ',        cls: 'bg-slate-800/60 text-slate-300 border-slate-500/40' },
    both: { label: 'Εντός & Εκτός ΓεΣΥ', cls: 'bg-sky-900/40 text-sky-300 border-sky-500/40' },
  };

  const Badges = ({ c }) => {
    const g = GESY[c.gesy_status];
    if (!g && !c.is_partner) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`collab-badges-${c.id}`}>
        {g && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${g.cls}`} data-testid={`collab-gesy-badge-${c.id}`}>
            <img src="/gesy-badge.png" alt="ΓεΣΥ" className="w-4 h-4 rounded-full object-cover" />
            {g.label}
          </span>
        )}
        {c.is_partner && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gold/60 bg-gold/15 text-gold text-[11px] font-semibold" data-testid={`collab-partner-badge-${c.id}`}>
            <img src="/jhouse-badge.png" alt="J.House" className="w-4 h-4 rounded-full object-contain bg-navy" />
            Συνεργάτης J.House
          </span>
        )}
      </div>
    );
  };

  const Avatar = ({ url, name, size = 'w-12 h-12', clickable = false }) => (
    url ? (
      <img
        src={photoSrc(url)}
        alt={name}
        onClick={clickable ? (e) => { e.stopPropagation(); setLightbox({ url, name }); } : undefined}
        className={`${size} rounded-full object-cover border border-gold/40 flex-shrink-0 ${clickable ? 'cursor-zoom-in hover:ring-2 hover:ring-gold/60 transition' : ''}`}
        title={clickable ? 'Κλικ για μεγέθυνση' : undefined}
        data-testid={clickable ? 'collab-avatar-zoom' : undefined}
      />
    ) : (
      <div className={`${size} rounded-full bg-navy-200/70 border border-gold/30 flex items-center justify-center text-gold text-sm font-bold flex-shrink-0`}>
        {(name || '?').trim().charAt(0).toUpperCase()}
      </div>
    )
  );

  return (
    <div className="rounded-2xl p-5 md:p-6" style={{ background: 'rgba(12,22,40,0.6)', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="line-collaborators-panel">
      <h3 className="text-gold text-lg font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>Συνεργάτες Γραμμής</h3>
      <p className="text-slate-400 text-xs mb-4">
        {readOnly
          ? 'Διαθέσιμοι συνεργάτες για παραπομπή ασθενών, ταξινομημένοι ανά πόλη.'
          : 'Κατάλογος ιατρών / επαγγελματιών υγείας για τη Γραμμή Ασθενή.'}
      </p>

      {!readOnly && (
        <form onSubmit={save} className="rounded-xl p-4 mb-4 bg-navy-200/30 border border-slate-700/60" data-testid="collaborator-form">
          <div className="flex items-center gap-4 mb-3">
            <Avatar url={form.photo_url} name={form.full_name} size="w-16 h-16" />
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files)} className="hidden" data-testid="collab-photo-input" id="collab-photo-file" />
              <label htmlFor="collab-photo-file" className="inline-block cursor-pointer px-3 py-1.5 rounded-lg text-xs text-gold border border-gold/50 hover:bg-gold/10 transition" data-testid="collab-photo-btn">
                {uploading ? 'Ανέβασμα…' : (form.photo_url ? '📷 Αλλαγή φωτογραφίας' : '📷 Ανέβασμα φωτογραφίας')}
              </label>
              {form.photo_url && <button type="button" onClick={() => setForm({ ...form, photo_url: '' })} className="ml-2 text-xs text-red-400 hover:text-red-300" data-testid="collab-photo-remove">Αφαίρεση</button>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
            <input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Ειδικότητα" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-specialty" />
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Ονοματεπώνυμο" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-name" />
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Πόλη" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-city" />
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Διεύθυνση" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-address" />
            <input value={form.phones} onChange={(e) => setForm({ ...form, phones: e.target.value })} placeholder="Τηλέφωνα" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-phones" />
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-email" />
          </div>
          <textarea value={form.treatments} onChange={(e) => setForm({ ...form, treatments: e.target.value })} placeholder="Θεραπείες που αναλαμβάνει (π.χ. φυσικοθεραπεία, αποκατάσταση, διατροφή...)" rows={2} className="w-full px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none resize-none mb-2" data-testid="collab-treatments" />

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
            <div className="flex items-center gap-2">
              <img src="/gesy-badge.png" alt="ΓεΣΥ" className="w-6 h-6 rounded-full object-cover border border-gold/30" />
              <select value={form.gesy_status} onChange={(e) => setForm({ ...form, gesy_status: e.target.value })} className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="collab-gesy-select">
                <option value="">ΓεΣΥ: — (καμία ένδειξη)</option>
                <option value="in">Εντός ΓεΣΥ</option>
                <option value="out">Εκτός ΓεΣΥ</option>
                <option value="both">Εντός & Εκτός ΓεΣΥ</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200 select-none" data-testid="collab-partner-label">
              <input type="checkbox" checked={!!form.is_partner} onChange={(e) => setForm({ ...form, is_partner: e.target.checked })} className="accent-gold w-4 h-4" data-testid="collab-partner-checkbox" />
              <img src="/jhouse-badge.png" alt="J.House" className="w-6 h-6 rounded-full object-contain bg-navy border border-gold/30" />
              Συνεργάτης J.House (εμφάνιση συμβόλου εταιρείας)
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-5 py-2 rounded-lg font-semibold text-navy text-sm" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="save-collab-btn">{editingId ? 'Ενημέρωση' : 'Προσθήκη'}</button>
            {editingId && <button type="button" onClick={cancelEdit} className="px-3 py-2 rounded-lg text-slate-300 text-sm border border-slate-600" data-testid="cancel-collab-btn">Άκυρο</button>}
          </div>
        </form>
      )}
      {msg && <p className="text-green-400 text-sm mb-2" data-testid="collab-success">{msg}</p>}
      {err && <p className="text-red-400 text-sm mb-2" data-testid="collab-error">{err}</p>}

      <div className="space-y-4" data-testid="collaborators-list">
        {items.length === 0 && <p className="text-slate-500 text-sm">Δεν υπάρχουν συνεργάτες ακόμη.</p>}
        {cityNames.map((city) => (
          <div key={city} data-testid={`collab-city-group-${city}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-gold/90 text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>📍 {city}</span>
              <span className="h-px flex-1 bg-gold/20" />
              <span className="text-slate-500 text-[11px]">{groups[city].length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {groups[city].map((c) => (
                <div key={c.id} onClick={() => setProfile(c)} className={`px-4 py-3 rounded-lg border flex items-start gap-3 cursor-pointer transition ${c.is_partner ? 'bg-gold/10 border-gold/50 hover:border-gold shadow-[0_0_0_1px_rgba(201,168,76,0.15)]' : 'bg-navy-200/40 border-slate-700/60 hover:border-gold/50'}`} data-testid={`collab-row-${c.id}`} title="Κλικ για προφίλ & επισκέψεις">
                  <Avatar url={c.photo_url} name={c.full_name} clickable />
                  <div className="min-w-0 flex-1">
                    <p className="text-gold text-xs font-semibold uppercase tracking-wide">{c.specialty}</p>
                    <p className="text-white text-sm font-medium truncate">{c.full_name}</p>
                    {c.treatments && <p className="text-slate-300 text-xs mt-1 leading-relaxed line-clamp-2">💊 {c.treatments}</p>}
                    {c.address && <p className="text-slate-400 text-xs mt-0.5">🏥 {c.address}</p>}
                    {c.phones && (
                      <p className="text-slate-300 text-xs mt-0.5">
                        📞 {c.phones.split(/[,/]/).map((p, i) => (
                          <a key={i} href={`tel:${p.trim()}`} onClick={(e) => e.stopPropagation()} className="text-gold hover:underline mr-2">{p.trim()}</a>
                        ))}
                      </p>
                    )}
                    {c.email && <p className="text-slate-300 text-xs mt-0.5">✉️ <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-gold hover:underline">{c.email}</a></p>}
                    <div className="flex items-center gap-2 mt-2 text-[11px]" data-testid={`collab-counts-${c.id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300">🟢 {c.counts?.attended || 0}</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-900/40 text-red-300">🔴 {c.counts?.missed || 0}</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-300">🟡 {c.counts?.pending || 0}</span>
                    </div>
                    <Badges c={c} />
                  </div>
                  {!readOnly && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); startEdit(c); }} className="text-gold/80 hover:text-gold text-xs border border-gold/40 rounded px-2 py-1" data-testid={`edit-collab-${c.id}`}>Επεξεργασία</button>
                      <button onClick={(e) => { e.stopPropagation(); remove(c.id, c.full_name); }} className="text-red-400 hover:text-red-300 text-xs border border-red-400/40 rounded px-2 py-1" data-testid={`delete-collab-${c.id}`}>Διαγραφή</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[10001] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="collab-lightbox"
        >
          <img
            src={photoSrc(lightbox.url)}
            alt={lightbox.name}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[82vh] rounded-xl border-2 border-gold/50 object-contain shadow-2xl"
          />
          <p className="text-gold mt-3 text-sm" style={{ fontFamily: 'Georgia, serif' }}>{lightbox.name}</p>
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-navy border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/10"
            data-testid="collab-lightbox-close"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}

      {profile && (
        <CollaboratorProfileModal
          collaborator={profile}
          isAdmin={!readOnly}
          onClose={() => setProfile(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default LineCollaboratorsPanel;
